/**
 * 조회조건 세션 유지 — [조회] 때 조회영역 값을 `MenuStorage.menuOnSession.setSearch` + URL 에 남기고, 진입 때 URL 에서
 * 한 번 되살린다(rgr02V2 `rules.ts` toSearchQuery/fromSearchQuery 방식). 키는 조회영역 값 버스의 키를 그대로 쓴다.
 * `Utils.qsStringify`(comma·encode:false) → `useParsedQuery`(comma) 왕복이라 목록 값은 하나씩 인코딩해 싣는다(화주명 `A,B` 보호).
 */
const LIST_KEYS = ['service_type', 'shipper_name', 'ORD_OPT_NO', 'REF_NO2', 'TRS_INVOICE_NO'] as const
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export type Rgr01SearchQuery = Partial<Record<(typeof LIST_KEYS)[number] | 'plnDateFrom' | 'plnDateTo' | 'LOT_ATTR5' | 'LOT_ATTR4', string | string[]>>

const cleanList = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string' || typeof x === 'number').map(String).filter(Boolean)
  return typeof v === 'string' && v.trim() ? [v.trim()] : []
}
const asText = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined)

/** 조회영역 값 → 저장 형태. 빈 값은 싣지 않는다 */
export const toSearchQuery = (fl: Record<string, unknown>): Rgr01SearchQuery => {
  const q: Rgr01SearchQuery = {}
  LIST_KEYS.forEach((k) => {
    const list = cleanList(fl[k])
    if (list.length) q[k] = list.map(encodeURIComponent)
  })
  const [from, to] = (fl.PLN_DATE as [string, string] | undefined) ?? []
  if (from && to) Object.assign(q, { plnDateFrom: from, plnDateTo: to })
  if (asText(fl.LOT_ATTR5)) q.LOT_ATTR5 = fl.LOT_ATTR5 as string
  if (asText(fl.LOT_ATTR4)) q.LOT_ATTR4 = fl.LOT_ATTR4 as string
  return q
}

/**
 * URL 쿼리 → 조회영역 값. 화주 조건이 없으면 복원하지 않는다(그런 조건은 [조회] 가 막아 저장되지 않는다 — 최초 진입).
 * 날짜가 빠지거나 형식이 어긋나면 PLN_DATE 를 비워 두고, 페이지가 기본 기간으로 채운다.
 */
export const fromSearchQuery = (q: Record<string, unknown>): Record<string, unknown> | undefined => {
  const lists = Object.fromEntries(LIST_KEYS.map((k) => [k, cleanList(q[k])]))
  if (!lists.service_type.length && !lists.shipper_name.length) return undefined
  const from = asText(q.plnDateFrom)
  const to = asText(q.plnDateTo)
  return {
    service_type: lists.service_type,
    // 화주명 '전체' 는 [''] 다 — 비어 있으면 셀렉트가 '전체' 를 가리키게 한다
    shipper_name: lists.shipper_name.length ? lists.shipper_name : [''],
    ...(from && to && DATE_RE.test(from) && DATE_RE.test(to) ? { PLN_DATE: [from, to] } : {}),
    ...(['ORD_OPT_NO', 'REF_NO2', 'TRS_INVOICE_NO'] as const).reduce((acc, k) => (lists[k].length ? { ...acc, [k]: lists[k] } : acc), {}),
    ...(asText(q.LOT_ATTR5) ? { LOT_ATTR5: q.LOT_ATTR5 } : {}),
    ...(asText(q.LOT_ATTR4) ? { LOT_ATTR4: q.LOT_ATTR4 } : {}),
  }
}
